import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Lock, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CustomerLogin({ onLoginSuccess }) {
  const [step, setStep] = useState(1); // 1: email, 2: PIN
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/customer/login`, {
        email: email
      });

      setMessage(response.data.message);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante l\'invio del PIN');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/customer/verify-pin`, {
        email: email,
        pin: pin
      });

      // Save session token
      localStorage.setItem('customer_session', response.data.session_token);
      localStorage.setItem('customer_user', JSON.stringify(response.data.user));

      // Call parent callback
      onLoginSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'PIN non valido');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendPin = () => {
    setStep(1);
    setPin('');
    setError('');
    setMessage('');
  };

  return (
    <div className="min-h-screen bg-[rgb(9,9,11)] flex items-center justify-center p-4">
      <div className="w-full max-width-md">
        <div className="bg-[rgb(24,24,27)] rounded-lg border border-[rgb(39,39,42)] p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              src="/assets/logo_gmed.svg" 
              alt="Gmed Logo" 
              className="h-16 mx-auto mb-4"
              data-testid="customer-gmed-logo"
            />
            <h1 className="text-3xl font-bold font-mono text-[rgb(250,250,250)] mb-2">
              Portale Clienti
            </h1>
            <p className="text-[rgb(161,161,170)] font-mono">
              MantisBT - Gestione Segnalazioni
            </p>
          </div>

          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-[rgb(161,161,170)]" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="inserisci@email.com"
                    required
                    className="pl-10 bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                    disabled={loading}
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500 rounded p-3">
                  <p className="text-red-500 text-sm font-mono">{error}</p>
                </div>
              )}

              {message && (
                <div className="bg-green-500/10 border border-green-500 rounded p-3">
                  <p className="text-green-500 text-sm font-mono">{message}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
                disabled={loading}
              >
                {loading ? 'Invio in corso...' : 'Invia PIN'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          )}

          {/* Step 2: PIN */}
          {step === 2 && (
            <form onSubmit={handlePinSubmit} className="space-y-6">
              <div className="bg-blue-500/10 border border-blue-500 rounded p-4 mb-4">
                <p className="text-blue-400 text-sm font-mono">
                  PIN inviato a: <strong>{email}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                  PIN (6 cifre)
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-[rgb(161,161,170)]" />
                  <Input
                    type="text"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    required
                    maxLength={6}
                    className="pl-10 bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono text-2xl tracking-widest text-center"
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500 rounded p-3">
                  <p className="text-red-500 text-sm font-mono">{error}</p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  type="submit"
                  className="w-full bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
                  disabled={loading || pin.length !== 6}
                >
                  {loading ? 'Verifica...' : 'Verifica PIN'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-[rgb(161,161,170)] hover:text-white font-mono"
                  onClick={handleResendPin}
                  disabled={loading}
                >
                  Richiedi nuovo PIN
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-[rgb(113,113,122)] font-mono">
            Accesso riservato ai clienti autorizzati
          </p>
        </div>
      </div>
    </div>
  );
}

export default CustomerLogin;
